

/* =============================================================================
   Premium Product Page — interactive modules
   Vanilla ES6+. No globals leaked. No jQuery. Event delegation where sensible.
   Each <premium-product-page> custom element boots its own scoped controllers,
   so multiple instances on a page never collide.
   ========================================================================== */
(() => {
  'use strict';

  const PREFERS_REDUCED_MOTION = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const ROOT_SELECTOR = 'premium-product-page';

  /* ---- small utilities --------------------------------------------------- */

  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const formatMoney = (cents, format) => {
    // Lightweight Shopify money formatter (no_decimals + with-comma support).
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (cents / 100).toFixed(
      /amount_no_decimals/.test(format || '') ? 0 : 2
    );
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = parts.join('.');
    if (!format) return '$' + amount;
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0]);
  };

  const announce = (root, message) => {
    const live = root.querySelector('[data-ppp-live]');
    if (live) live.textContent = message;
  };

  /* ---- Media gallery ----------------------------------------------------- */

  class Gallery {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-ppp-gallery-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-ppp-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-ppp-thumb]'));
      this.prevBtn = root.querySelector('[data-ppp-gallery-prev]');
      this.nextBtn = root.querySelector('[data-ppp-gallery-next]');
      this.zoomBtn = root.querySelector('[data-ppp-gallery-zoom]');
      this.lightbox = root.querySelector('[data-ppp-lightbox]');
      this.index = 0;
      if (!this.slides.length) return;
      this.bind();
      this.select(0, false);
    }

    bind() {
      if (this.prevBtn)
        this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn)
        this.nextBtn.addEventListener('click', () => this.step(1));

      // Thumbnail navigation via delegation.
      const thumbWrap = this.root.querySelector('[data-ppp-thumbs]');
      if (thumbWrap) {
        thumbWrap.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-ppp-thumb]');
          if (!thumb) return;
          this.select(this.thumbs.indexOf(thumb));
        });
        thumbWrap.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const thumb = e.target.closest('[data-ppp-thumb]');
          if (!thumb) return;
          e.preventDefault();
          this.select(this.thumbs.indexOf(thumb));
        });
      }

      // Keyboard arrows when the stage has focus.
      this.stage.setAttribute('tabindex', '0');
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.step(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.step(1);
        }
      });

      // Touch swipe.
      let startX = 0;
      let dx = 0;
      this.stage.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
          dx = 0;
        },
        { passive: true }
      );
      this.stage.addEventListener(
        'touchmove',
        (e) => {
          dx = e.touches[0].clientX - startX;
        },
        { passive: true }
      );
      this.stage.addEventListener('touchend', () => {
        if (Math.abs(dx) > 45) this.step(dx < 0 ? 1 : -1);
      });

      // Zoom / lightbox.
      if (this.zoomBtn && this.lightbox) {
        this.zoomBtn.addEventListener('click', () => this.openLightbox());
        this.lightbox.addEventListener('click', (e) => {
          if (
            e.target.closest('[data-ppp-lightbox-close]') ||
            e.target === this.lightbox
          )
            this.closeLightbox();
        });
        this.lightbox.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeLightbox();
        });
      }
    }

    step(dir) {
      const next = (this.index + dir + this.slides.length) % this.slides.length;
      this.select(next);
    }

    select(i, focusThumb = true) {
      this.index = i;
      this.slides.forEach((s, idx) => {
        const active = idx === i;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
      });
      this.thumbs.forEach((t, idx) => {
        const active = idx === i;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      if (focusThumb && this.thumbs[i]) this.thumbs[i].focus();
      // Lazy-load full-res when reached.
      const img = this.slides[i] && this.slides[i].querySelector('img[data-src]');
      if (img) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    }

    // Allow the variant picker to jump to a variant's featured media.
    selectByMediaId(mediaId) {
      const idx = this.slides.findIndex(
        (s) => s.dataset.mediaId === String(mediaId)
      );
      if (idx > -1) this.select(idx, false);
    }

    openLightbox() {
      if (!this.lightbox) return;
      const active = this.slides[this.index];
      const src =
        active.querySelector('img') && active.querySelector('img').currentSrc;
      const target = this.lightbox.querySelector('[data-ppp-lightbox-image]');
      if (target && src) target.src = src;
      this.lightbox.hidden = false;
      this.lightboxOpener = document.activeElement;
      requestAnimationFrame(() => {
        this.lightbox.classList.add('is-open');
        const close = this.lightbox.querySelector('[data-ppp-lightbox-close]');
        if (close) close.focus();
      });
      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
      const finish = () => {
        this.lightbox.hidden = true;
        if (this.lightboxOpener) this.lightboxOpener.focus();
      };
      if (PREFERS_REDUCED_MOTION) finish();
      else setTimeout(finish, 220);
    }
  }

  /* ---- Quantity selector ------------------------------------------------- */

  class Quantity {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-ppp-qty-input]');
      if (!this.input) return;
      root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ppp-qty-btn]');
        if (!btn) return;
        const dir = btn.dataset.pppQtyBtn === 'plus' ? 1 : -1;
        this.change(dir);
      });
      this.input.addEventListener('change', () => this.clamp());
    }

    get value() {
      return Math.max(1, parseInt(this.input.value, 10) || 1);
    }

    change(dir) {
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;
      let v = this.value + dir;
      v = Math.min(Math.max(v, min), max);
      this.input.value = v;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clamp() {
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;
      this.input.value = Math.min(Math.max(this.value, min), max);
    }
  }

  /* ---- Variant picker ---------------------------------------------------- */

  class VariantPicker {
    constructor(root, gallery) {
      this.root = root;
      this.gallery = gallery;
      this.form = root.querySelector('[data-ppp-product-form]');
      this.idInput = root.querySelector('[data-ppp-variant-id]');
      this.picker = root.querySelector('[data-ppp-variants]');
      this.dataEl = root.querySelector('[data-ppp-variant-json]');
      if (!this.dataEl) return;
      try {
        this.variants = JSON.parse(this.dataEl.textContent);
      } catch (_) {
        this.variants = [];
      }
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      if (this.picker) {
        this.picker.addEventListener('change', () => this.onChange());
        this.picker.addEventListener('click', (e) => {
          // Pill / swatch buttons that aren't native inputs.
          const opt = e.target.closest('[data-ppp-option-value]');
          if (!opt || opt.tagName === 'INPUT') return;
          const group = opt.closest('[data-ppp-option-group]');
          group
            .querySelectorAll('[data-ppp-option-value]')
            .forEach((b) => {
              b.classList.toggle('is-selected', b === opt);
              b.setAttribute('aria-checked', b === opt ? 'true' : 'false');
            });
          this.onChange();
        });
      }
      this.onChange(true);
    }

    selectedOptions() {
      const values = [];
      this.root
        .querySelectorAll('[data-ppp-option-group]')
        .forEach((group) => {
          const checked = group.querySelector(
            'input:checked, select, [aria-checked="true"]'
          );
          if (!checked) return;
          values.push(
            checked.tagName === 'SELECT' ? checked.value : checked.value || checked.dataset.optionValue
          );
        });
      return values;
    }

    match() {
      const selected = this.selectedOptions();
      // Single-variant products ("Default Title") don't render option groups,
      // so selectedOptions() returns []. Fall back to the variant whose id
      // matches the hidden input, or the first available variant.
      if (selected.length === 0) {
        if (this.idInput && this.idInput.value) {
          const id = parseInt(this.idInput.value, 10);
          const found = this.variants.find((v) => v.id === id);
          if (found) return found;
        }
        return this.variants.find((v) => v.available) || this.variants[0] || null;
      }
      return (
        this.variants.find((v) =>
          v.options.every((o, i) => o === selected[i])
        ) || null
      );
    }

    onChange(initial = false) {
      const variant = this.match();
      this.current = variant;
      this.updateSelectedLabels();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        // Notify external listeners (Shopify dynamic checkout, analytics)
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (!initial) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.updatePrice(variant);
      this.updateAvailability(variant);

      if (variant.featured_media && this.gallery) {
        this.gallery.selectByMediaId(variant.featured_media.id);
      }

      this.root.dispatchEvent(
        new CustomEvent('ppp:variant:change', {
          detail: { variant },
          bubbles: true,
        })
      );
    }



updateSelectedLabels() {
  this.root
    .querySelectorAll('[data-ppp-option-group]')
    .forEach((group) => {
      const label = group.querySelector('[data-ppp-option-selected]');
      if (!label) return;

      const checked = group.querySelector(
        'input:checked, [aria-checked="true"]'
      );
      const select = group.querySelector('select');

      const value = select
        ? select.value
        : (checked?.value || checked?.dataset.optionValue || '');

      label.textContent = value
        .replace(/[-_]/g, ' ')              // Replace - and _ with spaces
        .replace(/\s+/g, ' ')               // Remove duplicate spaces
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Title Case
    });
}


    updatePrice(variant) {
      const priceEl = this.root.querySelector('[data-ppp-price]');
      const compareEl = this.root.querySelector('[data-ppp-compare]');
      if (priceEl)
        priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      if (compareEl) {
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          compareEl.textContent = formatMoney(
            variant.compare_at_price,
            this.moneyFormat
          );
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    updateAvailability(variant) {
      const btn = this.root.querySelector('[data-ppp-add]');
      const text = btn && btn.querySelector('[data-ppp-add-text]');
      const stock = this.root.querySelector('[data-ppp-stock]');
      const buyNow = this.root.querySelector('[data-ppp-buy-now]');
      const available = variant.available;
      if (btn) {
        btn.disabled = !available;
        btn.classList.toggle('is-unavailable', !available);
      }
      if (buyNow) {
        buyNow.disabled = !available;
        buyNow.classList.toggle('is-unavailable', !available);
      }
      if (text)
        text.textContent = available
          ? btn.dataset.addLabel || 'Add to cart'
          : btn.dataset.soldOutLabel || 'Sold out';
      if (stock) {
        stock.classList.toggle('is-out', !available);
        const label = stock.querySelector('[data-ppp-stock-label]');
        if (label && stock.dataset.outLabel && !available)
          label.textContent = stock.dataset.outLabel;
      }
      announce(
        this.root,
        available ? 'Selected variant is available' : 'Selected variant is unavailable'
      );
    }

    setUnavailable() {
      const btn = this.root.querySelector('[data-ppp-add]');
      const text = btn && btn.querySelector('[data-ppp-add-text]');
      const buyNow = this.root.querySelector('[data-ppp-buy-now]');
      if (buyNow) buyNow.disabled = true;
      if (btn) btn.disabled = true;
      if (text) text.textContent = btn.dataset.unavailableLabel || 'Unavailable';
      announce(this.root, 'This combination is unavailable');
    }
  }

  /* ---- AJAX add to cart --------------------------------------------------- */

  class CartForm {
    constructor(root, quantity) {
      this.root = root;
      this.quantity = quantity;
      this.form = root.querySelector('[data-ppp-product-form]');
      this.addBtn = root.querySelector('[data-ppp-add]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }

    async submit(e) {
      e.preventDefault();
      if (!this.addBtn || this.addBtn.disabled) return;

      /* Delegate to the global cart service in helm.js. That service is the
         single source of truth for cart adds across the theme: it manages
         the spinner (`is-loading`/`aria-busy`), calls the AJAX API with the
         Section Rendering API to refresh the mini-cart, opens the cart
         drawer, and emits `helm:cart:added` / `helm:cart:error`. The native
         FormData is forwarded as-is so line-item properties, selling plans,
         and multi-variant adds keep working. */

      const onAdded = (ev) => {
        cleanup();
        announce(this.root, 'Added to cart');
        this.flashSuccess();
        // Back-compat for any listeners on the legacy event name.
        document.dispatchEvent(new CustomEvent('ppp:cart:added', { bubbles: true, detail: { item: ev && ev.detail && ev.detail.item } }));
      };
      const onError = (ev) => {
        cleanup();
        const err = ev && ev.detail && ev.detail.error;
        this.showError((err && (err.description || err.message)) || 'Could not add to cart');
      };
      const cleanup = () => {
        document.removeEventListener('helm:cart:added', onAdded);
        document.removeEventListener('helm:cart:error', onError);
      };
      document.addEventListener('helm:cart:added', onAdded, { once: true });
      document.addEventListener('helm:cart:error', onError, { once: true });

      document.dispatchEvent(new CustomEvent('helm:cart:add', {
        detail: {
          formData: new FormData(this.form),
          button: this.addBtn,
          source: 'ppp'
        }
      }));
    }

    setLoading(on) {
      this.addBtn.classList.toggle('is-loading', on);
      this.addBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    flashSuccess() {
      const text = this.addBtn.querySelector('[data-ppp-add-text]');
      if (!text) return;
      const original = text.textContent;
      text.textContent = this.addBtn.dataset.addedLabel || 'Added ✓';
      setTimeout(() => {
        text.textContent = original;
      }, 1600);
    }

    showError(message) {
      const err = this.root.querySelector('[data-ppp-cart-error]');
      if (err) {
        err.textContent = message;
        err.hidden = false;
      }
      announce(this.root, message);
    }
  }

  /* ---- Buy now (add + straight to checkout) ------------------------------- */

  /* Shopify swaps its own unbranded "Buy it now" button for a wallet button
     (Shop Pay / Google Pay / Apple Pay) the moment accelerated checkout is
     switched on, and there is no theme setting to get it back. This module
     provides an equivalent: POST the current form to /cart/add.js, then send
     the customer to /checkout. The cart drawer is deliberately not opened. */

  class BuyNow {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ppp-product-form]');
      this.btn = root.querySelector('[data-ppp-buy-now]');
      if (!this.form || !this.btn) return;
      this.btn.addEventListener('click', (e) => this.checkout(e));
    }

    get routeRoot() {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root.endsWith('/') ? root : root + '/';
    }

    async checkout(e) {
      e.preventDefault();
      if (this.btn.disabled) return;

      this.clearError();
      this.setLoading(true);

      try {
        const res = await fetch(this.routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });

        let data = {};
        try {
          data = await res.json();
        } catch (_) {
          /* non-JSON response — fall through to the generic message below */
        }

        if (!res.ok) {
          throw new Error(data.description || data.message || 'Could not add to cart');
        }

        /* Keep the spinner running through the navigation. */
        window.location.href = this.routeRoot + 'checkout';
      } catch (err) {
        this.setLoading(false);
        this.showError(err.message || 'Could not start checkout');
      }
    }

    setLoading(on) {
      this.btn.classList.toggle('is-loading', on);
      this.btn.setAttribute('aria-busy', on ? 'true' : 'false');
      this.btn.disabled = on;
    }

    clearError() {
      const err = this.root.querySelector('[data-ppp-cart-error]');
      if (err) {
        err.textContent = '';
        err.hidden = true;
      }
    }

    showError(message) {
      const err = this.root.querySelector('[data-ppp-cart-error]');
      if (err) {
        err.textContent = message;
        err.hidden = false;
      }
      announce(this.root, message);
    }
  }

  /* ---- Accordion --------------------------------------------------------- */

  // class Accordion {
  //   constructor(root) {
  //     this.root = root;
  //     this.wrap = root.querySelector('[data-ppp-accordion]');
  //     if (!this.wrap) return;
  //     this.single = this.wrap.dataset.single === 'true';
  //     this.items = Array.from(
  //       this.wrap.querySelectorAll('[data-ppp-accordion-item]')
  //     );
  //     this.wrap.addEventListener('click', (e) => {
  //       const trigger = e.target.closest('[data-ppp-accordion-trigger]');
  //       if (!trigger) return;
  //       this.toggle(trigger.closest('[data-ppp-accordion-item]'));
  //     });
  //   }

  //   toggle(item) {
  //     const trigger = item.querySelector('[data-ppp-accordion-trigger]');
  //     const panel = item.querySelector('[data-ppp-accordion-panel]');
  //     const open = trigger.getAttribute('aria-expanded') === 'true';

  //     if (this.single && !open) {
  //       this.items.forEach((other) => {
  //         if (other !== item) this.setState(other, false);
  //       });
  //     }
  //     this.setState(item, !open);
  //   }

  //   setState(item, open) {
  //     const trigger = item.querySelector('[data-ppp-accordion-trigger]');
  //     const panel = item.querySelector('[data-ppp-accordion-panel]');
  //     trigger.setAttribute('aria-expanded', String(open));
  //     item.classList.toggle('is-open', open);
  //     if (open) {
  //       panel.hidden = false;
  //       panel.style.maxHeight = PREFERS_REDUCED_MOTION
  //         ? 'none'
  //         : panel.scrollHeight + 'px';
  //     } else {
  //       if (!PREFERS_REDUCED_MOTION) panel.style.maxHeight = '0px';
  //       const finish = () => {
  //         panel.hidden = true;
  //         panel.style.maxHeight = '';
  //       };
  //       if (PREFERS_REDUCED_MOTION) finish();
  //       else setTimeout(finish, 260);
  //     }
  //   }
  // }







class Accordion {
  constructor(root) {
    this.root = root;
    this.wrappers = Array.from(root.querySelectorAll('[data-ppp-accordion]'));
    if (!this.wrappers.length) return;

    this.wrappers.forEach((wrap) => this.bindWrap(wrap));
  }

  bindWrap(wrap) {
    const single = wrap.dataset.single === 'true';
    const items = Array.from(wrap.querySelectorAll('[data-ppp-accordion-item]'));

    wrap.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-ppp-accordion-trigger]');
      if (!trigger || !wrap.contains(trigger)) return;

      const item = trigger.closest('[data-ppp-accordion-item]');
      if (!item) return;

      const open = trigger.getAttribute('aria-expanded') === 'true';

      if (single && !open) {
        items.forEach((other) => {
          if (other !== item) this.setState(other, false);
        });
      }

      this.setState(item, !open);
    });
  }

  setState(item, open) {
    const trigger = item.querySelector('[data-ppp-accordion-trigger]');
    const panel = item.querySelector('[data-ppp-accordion-panel]');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', String(open));
    item.classList.toggle('is-open', open);

    if (open) {
      panel.hidden = false;
      panel.style.maxHeight = PREFERS_REDUCED_MOTION
        ? 'none'
        : panel.scrollHeight + 'px';
    } else {
      if (!PREFERS_REDUCED_MOTION) panel.style.maxHeight = '0px';
      const finish = () => {
        panel.hidden = true;
        panel.style.maxHeight = '';
      };
      if (PREFERS_REDUCED_MOTION) finish();
      else setTimeout(finish, 260);
    }
  }
}


  /* ---- Promo banner ------------------------------------------------------ */

  class PromoBanner {
    constructor(root) {
      this.banner = root.querySelector('[data-ppp-promo]');
      if (!this.banner) return;
      this.key = 'ppp-promo-' + (this.banner.dataset.promoId || 'default');
      try {
        if (sessionStorage.getItem(this.key) === 'dismissed') {
          this.banner.hidden = true;
        }
      } catch (_) {}
      const close = this.banner.querySelector('[data-ppp-promo-dismiss]');
      if (close) close.addEventListener('click', () => this.dismiss());
    }

    dismiss() {
      this.banner.classList.add('is-dismissing');
      try {
        sessionStorage.setItem(this.key, 'dismissed');
      } catch (_) {}
      const finish = () => (this.banner.hidden = true);
      if (PREFERS_REDUCED_MOTION) finish();
      else setTimeout(finish, 240);
    }
  }

  /* ---- Horizontal slider (related products) ------------------------------ */

  class Slider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-ppp-slider-track]');
      this.prev = root.querySelector('[data-ppp-slider-prev]');
      this.next = root.querySelector('[data-ppp-slider-next]');
      this.counter = root.querySelector('[data-ppp-slider-counter]');
      if (!this.track) return;
      this.bind();
      this.update();
    }

    bind() {
      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 80), {
        passive: true,
      });
      window.addEventListener('resize', debounce(() => this.update(), 150));
    }

    cardWidth() {
      const card = this.track.querySelector('[data-ppp-slide-card]');
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return card.offsetWidth + gap;
    }

    scrollBy(dir) {
      this.track.scrollBy({
        left: this.cardWidth() * dir,
        behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.counter) {
        const per = Math.max(1, Math.round(this.track.clientWidth / this.cardWidth()));
        const total = this.track.querySelectorAll('[data-ppp-slide-card]').length;
        const current = Math.min(
          total,
          Math.round(this.track.scrollLeft / this.cardWidth()) + per
        );
        this.counter.textContent = `${current}/${total}`;
      }
    }
  }

  /* ---- Recommendations loader -------------------------------------------- */

  class Recommendations {
    constructor(root) {
      this.root = root;
      this.host = root.querySelector('[data-ppp-recommendations]');
      if (!this.host) return;
      const url = this.host.dataset.url;
      if (!url) return;
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting) {
            obs.disconnect();
            this.load(url);
          }
        },
        { rootMargin: '300px' }
      );
      observer.observe(this.host);
    }

    async load(url) {
      try {
        const res = await fetch(url);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const incoming = html.querySelector('[data-ppp-slider-track]');
        const target = this.host.querySelector('[data-ppp-slider-track]');
        if (incoming && target) {
          target.innerHTML = incoming.innerHTML;
          this.host.hidden = false;
          new Slider(this.host.closest('[data-ppp-slider]') || this.host);
        }
      } catch (_) {
        /* fail quietly; manual blocks remain as fallback */
      }
    }
  }

  /* ---- Mobile sticky add-to-cart ----------------------------------------- */

class StickyBar {
  constructor(root) {
    this.root = root;
    this.bar = root.querySelector('[data-ppp-sticky]');
    this.info = root.querySelector('.ppp__info');
    this.form = root.querySelector('[data-ppp-product-form]');
this.stickyBtn = root.querySelector('[data-ppp-sticky-add]');
    if (!this.bar || !this.info) return;

    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.threshold = this.parseThreshold(this.bar.dataset.threshold);
    this.ticking = false;
    this.visible = null;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(() => this.requestUpdate())
      : null;

    if (this.resizeObserver) {
      this.resizeObserver.observe(this.info);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.requestUpdate.bind(this), { passive: true });
    window.addEventListener('orientationchange', this.requestUpdate.bind(this), { passive: true });

    window.addEventListener('load', this.requestUpdate.bind(this), { once: true });
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.requestUpdate());
    }

    this.requestUpdate();
    if (this.stickyBtn) {
  this.stickyBtn.addEventListener('click', (e) => this.onStickyClick(e));
}
  }

  parseThreshold(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0.75;
    return Math.min(0.95, Math.max(0.5, n / 100));
  }

  requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  onScroll() {
    this.requestUpdate();
  }

  update() {
    const isMobile = this.mobileQuery.matches;
    if (!isMobile) {
      this.setVisible(false);
      return;
    }

    const rect = this.info.getBoundingClientRect();
    const infoTop = rect.top + window.scrollY;
    const triggerY = infoTop + (rect.height * 0.35);
    const shouldShow = window.scrollY >= triggerY;

    this.setVisible(shouldShow);
  }

  setVisible(visible) {
    if (this.visible === visible) return;
    this.visible = visible;
    this.bar.classList.toggle('is-visible', visible);
    this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
onStickyClick(e) {
  e.preventDefault();

  if (!this.form) return;

  document.dispatchEvent(
    new CustomEvent('helm:cart:add', {
      detail: {
        formData: new FormData(this.form),
        button: this.stickyBtn,
        source: 'sticky-bar'
      }
    })
  );
}
  disconnect() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.requestUpdate);
    window.removeEventListener('orientationchange', this.requestUpdate);
    this.resizeObserver?.disconnect();
  }
}
  /* ---- Share ------------------------------------------------------------- */

  class Share {
    constructor(root) {
      this.root = root;
      const wrap = root.querySelector('[data-ppp-share]');
      if (!wrap) return;
      wrap.addEventListener('click', async (e) => {
        const copy = e.target.closest('[data-ppp-share-copy]');
        if (copy) {
          e.preventDefault();
          try {
            await navigator.clipboard.writeText(copy.dataset.url || window.location.href);
            announce(root, 'Link copied to clipboard');
            copy.classList.add('is-copied');
            setTimeout(() => copy.classList.remove('is-copied'), 1500);
          } catch (_) {
            announce(root, 'Could not copy link');
          }
          return;
        }
        const native = e.target.closest('[data-ppp-share-native]');
        if (native && navigator.share) {
          e.preventDefault();
          navigator
            .share({ title: document.title, url: native.dataset.url })
            .catch(() => {});
        }
      });
    }
  }

  /* ---- Boot per custom element ------------------------------------------- */

  class PremiumProductPage extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      const gallery = new Gallery(this);
      const quantity = new Quantity(this);
      new VariantPicker(this, gallery);
      new CartForm(this, quantity);
      new BuyNow(this);
      new Accordion(this);
      new PromoBanner(this);
      this.querySelectorAll('[data-ppp-slider]').forEach((s) => new Slider(s));
      new Recommendations(this);
      new StickyBar(this);
      new Share(this);
    }
  }

  if (!customElements.get(ROOT_SELECTOR)) {
    customElements.define(ROOT_SELECTOR, PremiumProductPage);
  }
})();